import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Maximize2, RotateCcw, Trophy, Loader2, PlayCircle } from "lucide-react";
import { QuestionCard } from "@/components/learn/VideoQuestionCard";
import { QuestionTimeline } from "@/components/learn/QuestionTimeline";
import { VideoQuizRecap } from "@/components/learn/VideoQuizRecap";

const LETTERS = "ABCDEF";
const play = (v) => v?.play()?.catch(() => {});
// Learner (cookie) API — the staff `@/utils/api` client points at /api and uses staff auth.
const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/lms`, withCredentials: true });

// Video that pauses at each checkpoint until the learner answers; blocks skipping ahead.
export const InteractiveVideo = ({ lesson, src, onFinished }) => {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const cps = lesson.video_checkpoints;
  const [answers, setAnswers] = useState({});
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [best, setBest] = useState(lesson.video_quiz_best);
  const [started, setStarted] = useState(false);
  const [time, setTime] = useState({ now: 0, duration: 0 });

  const nextOpen = () => cps.find((c) => !answers[c.id]);
  const onTime = () => {
    const v = videoRef.current; const n = nextOpen();
    setTime({ now: v.currentTime, duration: v.duration || 0 });
    if (n && !active && v.currentTime >= n.time) { v.pause(); v.currentTime = n.time; setActive(n); }
  };
  const onSeeking = () => {
    const v = videoRef.current; const n = nextOpen();
    if (n && v.currentTime > n.time + 0.25) { v.currentTime = n.time; toast.info("Answer the question first to carry on"); }
  };
  const onPlay = () => { setStarted(true); if (active && !answers[active.id]) videoRef.current.pause(); };

  const choose = async (idx) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/learner/lessons/${lesson.id}/video-quiz/check`, { checkpoint_id: active.id, selected_index: idx });
      setAnswers((a) => ({ ...a, [active.id]: { selected: idx, ...data } }));
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't check answer"); }
    finally { setBusy(false); }
  };
  const resume = () => { setActive(null); play(videoRef.current); };
  const onEnded = async () => {
    if (cps.some((c) => !answers[c.id])) return;
    try {
      const { data } = await api.post(`/learner/lessons/${lesson.id}/video-quiz/finish`, { answers: cps.map((c) => ({ checkpoint_id: c.id, selected_index: answers[c.id].selected })) });
      setResult(data); setBest({ score: data.best_score, total: data.total }); if (data.passed) onFinished?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't save your score"); }
  };
  const restart = () => { setAnswers({}); setResult(null); setActive(null); const v = videoRef.current; v.currentTime = 0; play(v); };
  // Re-answer from question i onwards; earlier answers from this run are kept.
  const jumpTo = (i) => {
    const keep = Object.fromEntries(cps.slice(0, i).map((c) => [c.id, answers[c.id]]));
    setAnswers(keep); setResult(null); setActive(null);
    const v = videoRef.current; v.currentTime = Math.max(i ? cps[i - 1].time + 0.5 : 0, cps[i].time - 6); play(v);
  };
  const fullscreen = () => (document.fullscreenElement ? document.exitFullscreen() : wrapRef.current?.requestFullscreen?.());

  const done = Object.keys(answers).length;
  return (
    <div className="mb-4 space-y-2" data-testid="interactive-video">
      <div ref={wrapRef} className="relative aspect-video overflow-hidden rounded-md border border-border bg-black">
        <video ref={videoRef} src={src} controls controlsList="nofullscreen nodownload" disablePictureInPicture playsInline
          onTimeUpdate={onTime} onLoadedMetadata={(e) => setTime({ now: 0, duration: e.target.duration || 0 })} onSeeking={onSeeking} onPlay={onPlay} onEnded={onEnded} className="h-full w-full" data-testid="interactive-video-player" />
        {!started && !result && (
          <button onClick={() => play(videoRef.current)} className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/50 text-white" data-testid="interactive-video-start">
            <PlayCircle size={56} /><span className="text-sm font-semibold">Start the video quiz · {cps.length} question{cps.length === 1 ? "" : "s"}</span>
          </button>
        )}
        {active && <QuestionCard cp={active} index={cps.indexOf(active)} total={cps.length} answer={answers[active.id]} busy={busy} onChoose={choose} onContinue={resume} letters={LETTERS} />}
        {result && (
          <div className="absolute inset-0 flex overflow-y-auto bg-[#0b2545]/95 p-3 text-white sm:p-6" data-testid="video-quiz-result">
          <div className="m-auto flex w-full flex-col items-center gap-2.5 text-center sm:gap-3">
            <Trophy size={32} className="text-amber-300" />
            <p className="font-heading text-3xl font-bold" data-testid="video-quiz-score">{result.score} / {result.total}</p>
            {result.passed
              ? <p className="text-sm text-white/80" data-testid="video-quiz-passed">{result.score === result.total ? "Retrofit pro — full marks!" : "Passed — well done!"} Best score: {result.best_score}/{result.total}</p>
              : <p className="rounded-lg bg-rose-500/20 px-3 py-1.5 text-sm font-medium text-rose-100" data-testid="video-quiz-failed">You need {result.pass_mark}/{result.total} to pass — watch again to complete this lesson.</p>}
            <button onClick={restart} className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#0b2545] hover:bg-white/90" data-testid="video-quiz-retry"><RotateCcw size={14} /> Watch again</button>
            <p className="text-[11px] text-white/60">Review your answers below the video</p>
          </div>
          </div>
        )}
        {!active && !result && <button onClick={fullscreen} className="absolute right-2 top-2 rounded-md bg-black/50 p-1.5 text-white hover:bg-black/70" title="Full screen" data-testid="interactive-video-fullscreen"><Maximize2 size={14} /></button>}
      </div>
      <QuestionTimeline cps={cps} answers={answers} now={time.now} duration={time.duration} />
      {result && (
        <div className="space-y-3 rounded-xl bg-[#0b2545] p-4 text-white" data-testid="video-quiz-review">
          <p className="text-sm font-semibold">Review your answers <span className="font-normal text-white/60">· {result.score}/{result.total} correct</span></p>
          <VideoQuizRecap cps={cps} answers={answers} onJump={jumpTo} />
          <button onClick={restart} className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#0b2545] hover:bg-white/90" data-testid="video-quiz-retry-bottom"><RotateCcw size={14} /> Watch again</button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600" data-testid="video-quiz-status">
        <span className="font-medium">Video quiz: {done}/{cps.length} answered</span>
        {cps.map((c, i) => {
          const a = answers[c.id];
          return <span key={c.id} className="inline-flex items-center gap-0.5">{a ? (a.correct ? <CheckCircle2 size={12} className="text-green-600" /> : <XCircle size={12} className="text-rose-600" />) : <span className="h-2.5 w-2.5 rounded-full border border-slate-300" />} Q{i + 1}</span>;
        })}
        {lesson.video_pass_mark && <span className="text-slate-500" data-testid="video-quiz-pass-mark">Pass mark {lesson.video_pass_mark}/{cps.length}</span>}
        {best && <span className={`ml-auto font-medium ${!lesson.video_pass_mark || best.score >= lesson.video_pass_mark ? "text-green-700" : "text-amber-700"}`} data-testid="video-quiz-best">Best score {best.score}/{best.total}</span>}
        {busy && <Loader2 size={12} className="animate-spin" />}
      </div>
    </div>
  );
};
